import {Component, Input, OnDestroy, OnInit} from '@angular/core'
import {AnalyseRow, AnalyseRuleTable, RuleType} from '../common/domain/analyse'
import {Subscription} from 'rxjs'
import {PermissionsService} from '../services/permissions.service'
import {ChangeDetails} from '../common/domain/change'
import {ChangeService} from '../services/change.service'
import {
  AddEditWhitelistDetails,
  AnalysisListAddEditRowDialogComponent,
  FieldValue,
} from '../dialogs/analysis-list-add-edit-dialog.component'
import {AddAnalysisRuleDetails, AnalysisAddRuleDialogComponent} from '../dialogs/analysis-add-rule-dialog.component'
import {MatDialog, MatDialogRef} from '@angular/material/dialog'
import {DocgenConfig} from '../common/domain/docgen-config'
import {ToastrService} from 'ngx-toastr'
import {AnalyseConfig} from '../common/domain/config'

class TableDef {
  visible: boolean
  ruleType: RuleType
  name: string
  json: string
  rules: AnalyseRuleTable[]

  constructor(visible: boolean, name: string, json: string, ruleType: RuleType) {
    this.visible = visible
    this.name = name
    this.ruleType = ruleType
    this.setJson(json)
  }

  setJson: (json: string) => void = (json: string) => {
    // todo prettify json?
    this.json = json
    try {
      const rules = this.fixFieldName(JSON.parse(json))
      this.rules = rules
      this.visible = true

    } catch (e) {
      this.rules = []
      this.visible = false
    }
  }

  fixFieldName(rules: AnalyseRuleTable[]): AnalyseRuleTable[] {
    return rules.map(table => {
      const result: AnalyseRuleTable = Object.assign({}, table, {
        // todo find permanent fix to the prettifying problem - perhaps maintain original and displayable versions of the xpaths?
        // fields: table.fields.map(field => {
        //   if (field.toLowerCase().startsWith('count(')) {
        //     return field.replace(/count\(.*[^\\/]\/(.+)\)$/, 'count($1)')
        //   }
        //   return field.replace(/.*[^\\/]\/(.+)$/, '$1')
        // }),

      })
      return result
    })
  }
}

@Component({
  selector: 'app-analysis-rules',
  templateUrl: './analysis-config.component.html',
  styleUrls: ['./analysis-config.component.scss'],
})
export class AnalysisConfigComponent implements OnInit, OnDestroy {

  /** Path identifying the configuration file, injected */
  @Input() configPath: string
  /** Whether product or Solo analysis, injected */
  @Input() analysisStage: 'analysePayload' | 'analyseEnriched'

  /** Analysis configuration, set on init before any changes are made */
  config: AnalyseConfig
  /** Representation of the analysis rule sets, used for displaying the rules */
  rulesets: TableDef[]

  canChangeSkippedStatus = false
  isSkipped = false
  editingJson = false
  canAddRules = false

  subs = new Subscription()

  constructor(
    private dialog: MatDialog,
    private permissionsService: PermissionsService,
    private changeService: ChangeService,
    private toast: ToastrService,
  ) {
  }

  ngOnInit(): void {
    this.subs.add(
      this.permissionsService.deleteRule.subscribe((permission: boolean) => this.canChangeSkippedStatus = permission),
    )

    this.subs.add(this.permissionsService.addRules.subscribe((permission: boolean) => this.canAddRules = permission))
  }

  ngOnDestroy(): void {
    this.saveChanges()
    this.subs.unsubscribe()
  }

  toggleSkipped(): void {
    this.isSkipped = !this.isSkipped
    this.saveChanges()
  }

  toggleJsonEditor(): void {
    if (this.editingJson) {
      const areAllRulesetsOk = this.rulesets.every((ruleset: TableDef) => {
        try {
          const rulesetRules = JSON.parse(ruleset.json) as any[]
          return rulesetRules.every(rule => {
            try {
              JSON.parse(JSON.stringify(rule, undefined, 2))
              return true
            } catch (e) {
              return false
            }
          })
        } catch (e) {
          return false
        }
      })

      if (areAllRulesetsOk) {
        // ok to switch to table view mode
        this.saveChanges()
        this.editingJson = false
      } else {
        this.toast.error(`JSON does not produce valid rulesets`)
        return
      }

    } else {
      // Always ok to switch from table view mode to JSON editing mode
      this.editingJson = true
    }
  }

  @Input()
  set analyseConfig(data: AnalyseConfig | string) {
    if (!data) {
      this.config = undefined
      this.rulesets = []
      return
    }
    if (typeof data === 'object') {
      this.config = data
    } else {
      this.config = JSON.parse(data) as AnalyseConfig
    }

    this.isSkipped = this.config.skip

    if (this.config && this.config.rules) {
      this.rulesets = [
        new TableDef(
          this.config.rules.filter(r => r.ruleType = 'notSupported').length > 0,
          'Not Supported Yet',
          JSON.stringify(this.config.rules.filter(r => r.ruleType = 'notSupported'), undefined, 2),
          'notSupported',
        ),
        new TableDef(
          this.config.rules.filter(r => r.ruleType = 'blacklist').length > 0,
          'Blacklist',
          JSON.stringify(this.config.rules.filter(r => r.ruleType = 'blacklist'), undefined, 2),
          'blacklist',
        ),
        new TableDef(
          this.config.rules.filter(r => r.ruleType = 'validation').length > 0,
          'Validation',
          JSON.stringify(this.config.rules.filter(r => r.ruleType = 'validation'), undefined, 2),
          'validation',
        ),
        new TableDef(
          this.config.rules.filter(r => r.ruleType = 'whitelist').length > 0,
          'Whitelist',
          JSON.stringify(this.config.rules.filter(r => r.ruleType = 'whitelist'), undefined, 2),
          'whitelist',
        ),
      ]
    } else {
      this.rulesets = []
    }
  }

  /**
   * Add a new rule, with no expected values.
   *
   * @param ruleset represents all the rules within a particular ruleset (i.e. within the whitelist, say).
   */
  addNewRule(ruleset: TableDef): void {
    const dialogRef = this.dialog.open(AnalysisAddRuleDialogComponent, {
      width: '750px',
      data: {
        title: 'Create Analysis Rule',
        name: undefined,
        description: undefined,
        mnemonic: undefined,
        fields: undefined, // no fields, initially...
        placeholderValue: 'Replace with XPath 1.0 expression...',
      } as AddAnalysisRuleDetails,
      panelClass: 'dialogFormField500',
    })

    dialogRef.afterClosed().subscribe(async (data: AddAnalysisRuleDetails) => {
      if (data) {
        if (!data.name) {
          this.toast.error(`Rule name must be supplied`)
          return
        }
        // todo validate name is unique??
        if (!data.mnemonic) {
          this.toast.error(`Rule mnemonic must be supplied`)
          return
        }
        // todo validate mnemonic is unique??
        const fields = data.fields ? data.fields.filter((field: string) => field) : []
        if (fields.length < 1) {
          this.toast.error(`At least one field xpath must be supplied`)
          return
        }

        ruleset.rules.push({
          ruleType: ruleset.ruleType,
          name: data.name,
          description: data.description || undefined,
          mnemonic: data.mnemonic,
          fields: fields,
          rule: [],
        } as AnalyseRuleTable)

        ruleset.setJson(JSON.stringify(ruleset.rules, undefined, 2))
        this.saveChanges()
      }
    })
  }

  /**
   * Add a row (a set of expected values) to a particular rule within the ruleset.
   *
   * @param rule the rule (i.e. table) to which the new row should be added.
   * @param ruleset represents all the rules within a particular ruleset (i.e. within the whitelist, say).
   */
  addToList(rule: AnalyseRuleTable, ruleset: TableDef): void {
    const fields = []
      .concat({fieldName: 'description', fieldValue: undefined})
      .concat({fieldName: 'test', fieldValue: undefined})
      .concat(rule.fields.map((field: string) => {
          return {
            fieldName: field,
            fieldValue: undefined,
          }
        }),
      )

    const stageName = this.analysisStage === 'analysePayload' ? 'Product' : 'Solo'
    const dialogRef = this.displayEditingDialog(rule.name, rule.mnemonic, fields, `Add To ${stageName} ${ruleset.name}`)
    dialogRef.afterClosed().subscribe(async (data: AddEditWhitelistDetails) => {
      if (data) {
        const nextMnemonic = this.getNextRowMnemonic(rule)
        rule.rule.push(
          {
            mnemonic: nextMnemonic,
            description: data.fieldValues.find((field: FieldValue) => field.fieldName === 'description').fieldValue || undefined,
            test: data.fieldValues.find((field: FieldValue) => field.fieldName === 'test').fieldValue || undefined,
            expected: data.fieldValues
              .filter((field: FieldValue) => field.fieldName !== 'description')
              .filter((field: FieldValue) => field.fieldName !== 'test')
              .map((field: FieldValue) => field.fieldValue || ''),
          } as AnalyseRow,
        )
        ruleset.setJson(JSON.stringify(ruleset.rules, undefined, 2))
        this.saveChanges()
      }
    })
  }

  /**
   * Edit a row (a set of expected values) in a particular rule within the ruleset.
   *
   * @param row the expected values being edited.
   * @param rule the rule (i.e. table) to which the row belongs.
   * @param ruleset represents all the rules within a particular ruleset (i.e. within the whitelist, say).
   */
  editListEntry(row: AnalyseRow, rule: AnalyseRuleTable, ruleset: TableDef): void {
    const fields = []
      .concat({fieldName: 'description', fieldValue: row.description || undefined})
      .concat({fieldName: 'test', fieldValue: row.test || undefined})
      .concat(rule.fields.map((field: string, index: number) => {
          return {
            fieldName: field,
            fieldValue: row.expected[index],
          }
        }),
      )

    const stageName = this.analysisStage === 'analysePayload' ? 'Product' : 'Solo'
    const dialogRef = this.displayEditingDialog(rule.name, rule.mnemonic, fields, `Edit ${stageName} ${ruleset.name} Entry`)
    dialogRef.afterClosed().subscribe(async (data: AddEditWhitelistDetails) => {
      if (data) {
        const rowToBeEdited = rule.rule.find((rule: AnalyseRow) => rule.mnemonic === row.mnemonic)
        row.description = data.fieldValues.find((field: FieldValue) => field.fieldName === 'description').fieldValue || undefined
        row.test = data.fieldValues.find((field: FieldValue) => field.fieldName === 'test').fieldValue || undefined
        data.fieldValues
          .filter((field: FieldValue) => field.fieldName !== 'description')
          .filter((field: FieldValue) => field.fieldName !== 'test')
          .forEach((field: FieldValue, index: number) => rowToBeEdited.expected[index] = field.fieldValue)
        ruleset.setJson(JSON.stringify(ruleset.rules, undefined, 2))
        this.saveChanges()
      }
    })
  }

  private saveChanges(): void {
    if (this.isSkipped !== this.config.skip) {
      this.config.skip = this.isSkipped
    }

    this.config.rules = this.rulesets.reduce((a, r) => {
      return a.concat(r.rules)
    }, [])

    const currentConfigObject = this.changeService.getObject(this.configPath)
    const currentConfig = JSON.parse(currentConfigObject.content) as DocgenConfig

    currentConfig[this.analysisStage] = this.config
    const changedConfigObject = {
      name: currentConfigObject.name,
      path: currentConfigObject.path,
      content: JSON.stringify(currentConfig, undefined, 2),
      size: undefined,
      hash: undefined,
      modified: undefined,
      state: undefined,
    } as ChangeDetails

    this.changeService.addChange(changedConfigObject)
  }

  private displayEditingDialog(ruleName: string, ruleMnemonic: string, fields: FieldValue[], title: string): MatDialogRef<AnalysisListAddEditRowDialogComponent, any> {
    return this.dialog.open(AnalysisListAddEditRowDialogComponent, {
      width: '750px',
      data: {
        title: title,
        ruleName: ruleName,
        ruleMnemonic: ruleMnemonic,
        fieldValues: fields,
      } as AddEditWhitelistDetails,
      panelClass: 'dialogFormField500',
    })
  }

  /**
   * Generate the next mnemonic in numerical sequence for the supplied {@link AnalyseRuleTable}.
   *
   * This assumes that all existing mnemonics are string representations of integers, as has been the custom but which is not
   * enforced by the code, and returns a four-digit-long string of the next integer in sequence.
   */
  private getNextRowMnemonic(ruleTable: AnalyseRuleTable): string {
    const sortedNumbers = ruleTable.rule
      .map((rule: AnalyseRow) => rule.mnemonic)
      .map((mnemonicAsString: string) => Number(mnemonicAsString))
      .sort((a, b) => a - b)

    return sortedNumbers.length === 0 ? '0001' : String((sortedNumbers[sortedNumbers.length - 1] + 1)).padStart(4, '0')
  }
}
